import React from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { ChartSpec } from "./types";

// Categorical palette reused from MetricsPage charts. Recharts will cycle
// through this for multi-series.
const SERIES_COLORS = [
  "#7c3aed",
  "#3949ab",
  "#10b981",
  "#f59e0b",
  "#ef4444",
  "#0ea5e9",
  "#a855f7",
  "#84cc16",
  "#f97316",
  "#14b8a6",
];

interface Props {
  spec: ChartSpec;
}

const ChartCanvas: React.FC<Props> = ({ spec }) => {
  if (spec.type === "kpi") {
    return (
      <div
        style={{
          padding: 16,
          textAlign: "center",
        }}
      >
        <div style={{ fontSize: 12, color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.4 }}>
          {spec.title || "Value"}
        </div>
        <div style={{ fontSize: 36, fontWeight: 700, color: "#111827", marginTop: 4 }}>
          {String(spec.value)}
        </div>
        {spec.subtitle ? (
          <div style={{ fontSize: 12, color: "#6b7280", marginTop: 4 }}>{spec.subtitle}</div>
        ) : null}
      </div>
    );
  }

  if (spec.type === "pie") {
    return (
      <div style={{ width: "100%", height: 280 }}>
        <ResponsiveContainer>
          <PieChart>
            <Tooltip />
            <Legend />
            <Pie
              data={spec.data}
              dataKey={spec.valueKey}
              nameKey={spec.nameKey}
              outerRadius={100}
              isAnimationActive={false}
              label
            >
              {spec.data.map((_, i) => (
                <Cell key={i} fill={SERIES_COLORS[i % SERIES_COLORS.length]} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // line / bar / stacked_bar share a layout.
  const stack = spec.type === "stacked_bar" ? "all" : undefined;
  return (
    <div style={{ width: "100%", height: 280 }}>
      <ResponsiveContainer>
        {spec.type === "line" ? (
          <LineChart data={spec.data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey={spec.xKey} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            {spec.yKeys.map((k, i) => (
              <Line
                key={k}
                type="monotone"
                dataKey={k}
                stroke={SERIES_COLORS[i % SERIES_COLORS.length]}
                isAnimationActive={false}
                dot={false}
              />
            ))}
          </LineChart>
        ) : (
          <BarChart data={spec.data} margin={{ top: 8, right: 16, left: 0, bottom: 4 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
            <XAxis dataKey={spec.xKey} tick={{ fontSize: 11 }} />
            <YAxis tick={{ fontSize: 11 }} />
            <Tooltip />
            <Legend />
            {spec.yKeys.map((k, i) => (
              <Bar
                key={k}
                dataKey={k}
                fill={SERIES_COLORS[i % SERIES_COLORS.length]}
                stackId={stack}
                isAnimationActive={false}
              />
            ))}
          </BarChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default ChartCanvas;
